import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getRankTier } from './constants';
import RankShield from './RankShield';
import './RankRevealModal.css';

/**
 * Full-screen modal celebrating a player reaching ranked status (5 matches).
 * Shows rank tier with shield animation, ELO number, and a dismiss button.
 *
 * @param {object} props - Component props.
 * @param {number} props.elo - The player's current ELO rating.
 * @param {string} [props.rank] - Optional rank name override.
 * @param {Function} props.onDismiss - Callback when the modal is dismissed.
 */
export default function RankRevealModal({ elo, rank, onDismiss }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('intro');
  const tier = rank ? getRankTier(elo) : getRankTier(elo);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('reveal'), 800);
    const t2 = setTimeout(() => setPhase('details'), 1800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const handleDismiss = useCallback(() => {
    if (onDismiss) onDismiss();
  }, [onDismiss]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleDismiss]);

  return (
    <div className="rank-reveal-overlay" onClick={handleDismiss}>
      <div className="rank-reveal-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`rank-reveal-content phase-${phase}`}>
          <div className="rank-reveal-label">{t('rankReveal.ranked')}</div>

          <div className="rank-reveal-shield-container">
            <div className="rank-reveal-glow" style={{ background: `radial-gradient(circle, ${tier.color}40, transparent 70%)` }} />
            <RankShield tier={tier} size={96} />
          </div>

          <div className="rank-reveal-tier" style={{ color: tier.color }}>
            {tier.name}
          </div>

          <div className="rank-reveal-elo">
            {elo} ELO
          </div>

          <div className="rank-reveal-subtitle">
            {t('rankReveal.subtitle')}
          </div>

          <button className="rank-reveal-btn" onClick={handleDismiss}>
            {t('rankReveal.continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
